import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Loader2, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import api from '../services/api';

/**
 * Report Reconciliation — INTERNAL-ONLY testing/diagnostic tool.
 *
 * Compares the existing Summary Stock Audit Report (the operational
 * benchmark during Stocktake testing) against this session's own Step 7
 * calculation. Read-only: never edits either source, never persists a
 * link, and is only ever mounted from the internal Report Centre — not
 * reachable from the Client Portal.
 */

function fmtValue(v, unit) {
  if (v === null || v === undefined) return '—';
  if (unit === 'percent') return `${Number(v).toFixed(2)}%`;
  if (unit === 'days') return Number(v).toFixed(1);
  return `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function diagnosticBadge(diagnostic, comparable) {
  if (!comparable) {
    return <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50">Not comparable</Badge>;
  }
  if (diagnostic?.startsWith('match')) {
    return <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50">Match</Badge>;
  }
  if (diagnostic?.includes('sign/presentation')) {
    return <Badge variant="outline" className="text-blue-700 border-blue-300 bg-blue-50">Sign difference</Badge>;
  }
  if (diagnostic?.includes('missing')) {
    return <Badge variant="outline" className="text-slate-700 border-slate-300 bg-slate-50">Missing input</Badge>;
  }
  return <Badge variant="outline" className="text-rose-700 border-rose-300 bg-rose-50">Needs review</Badge>;
}

function CandidateCard({ candidate, dateDiffDays, onConfirm, confirming }) {
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="reconciliation-candidate-card">
      <div className="flex items-center gap-2 text-amber-800 text-sm font-medium">
        <AlertTriangle className="h-4 w-4" />
        Suggested Match — Confirmation Required
      </div>
      <p className="text-xs text-amber-700 mt-1">
        No exact link was found. This is the closest Summary Report by date, {dateDiffDays != null ? `${dateDiffDays} day(s) away` : ''} — it has NOT been used as the benchmark yet.
      </p>
      <div className="text-xs mt-2 space-y-0.5 text-slate-700">
        <div><span className="font-medium">Stock #:</span> {candidate.stock_number}</div>
        <div><span className="font-medium">Audit Date:</span> {candidate.audit_date} (period start {candidate.previous_audit_date || '—'})</div>
        <div><span className="font-medium">Scope:</span> {candidate.scope} · <span className="font-medium">Status:</span> {candidate.status}</div>
      </div>
      <Button
        size="sm"
        className="mt-2"
        onClick={() => onConfirm(candidate.id)}
        disabled={confirming}
        data-testid={`reconciliation-confirm-candidate-${candidate.id}`}
      >
        {confirming ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
        Confirm &amp; Use as Benchmark
      </Button>
    </div>
  );
}

export default function ReportReconciliationDialog({ open, onOpenChange, sessionId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [error, setError] = useState(null);

  const fetchReconciliation = (confirmId = null) => {
    setLoading(true);
    setError(null);
    const params = confirmId ? { confirm_summary_report_id: confirmId } : {};
    api.get(`/stocktake/sessions/${sessionId}/reconciliation`, { params })
      .then(res => setData(res.data))
      .catch(err => setError(err?.response?.data?.detail || err.message))
      .finally(() => {
        setLoading(false);
        setConfirming(null);
      });
  };

  useEffect(() => {
    if (!open || !sessionId) return;
    setData(null);
    fetchReconciliation();
  }, [open, sessionId]);

  const handleConfirm = (candidateId) => {
    setConfirming(candidateId);
    fetchReconciliation(candidateId);
  };

  const link = data?.link;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="reconciliation-dialog">
        <DialogHeader>
          <DialogTitle>Report Reconciliation</DialogTitle>
          <DialogDescription>
            Internal testing tool — compares the existing Summary Audit Report (benchmark) against this Stocktake session. Never affects the Summary Wizard, Client Portal, or client-facing PDFs.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-10" data-testid="reconciliation-loading">
            <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
          </div>
        )}

        {!loading && error && (
          <p className="text-sm text-rose-600" data-testid="reconciliation-error">{error}</p>
        )}

        {!loading && !error && data && (
          <div className="space-y-3" data-testid="reconciliation-content">
            {link?.status === 'none' && (
              <p className="text-sm text-slate-600" data-testid="reconciliation-none">
                No linked Summary Report found for this session (no exact or nearby-date Summary Report exists for this site).
              </p>
            )}

            {link?.status === 'suggested' && !data.comparison && (
              <CandidateCard
                candidate={link.candidate}
                dateDiffDays={link.date_diff_days}
                onConfirm={handleConfirm}
                confirming={confirming === link.candidate?.id}
              />
            )}

            {link?.status === 'ambiguous' && !data.comparison && (
              <div className="space-y-2" data-testid="reconciliation-ambiguous">
                <div className="flex items-center gap-2 text-amber-800 text-sm font-medium">
                  <AlertTriangle className="h-4 w-4" />
                  Multiple exact-date matches — pick one to confirm
                </div>
                {(link.candidates || []).map(c => (
                  <CandidateCard key={c.id} candidate={c} onConfirm={handleConfirm} confirming={confirming === c.id} />
                ))}
              </div>
            )}

            {(link?.status === 'linked' || data.comparison) && (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    <span className="font-medium">Benchmark:</span>
                    <Badge variant="outline" data-testid="reconciliation-benchmark-badge">{data.benchmark_report?.stock_number}</Badge>
                    <Badge variant="secondary">
                      {link.match_type === 'job_id' ? 'Linked by Job'
                        : link.match_type === 'exact_date_scope' ? 'Exact Date/Scope Match'
                        : 'Suggested Match — Confirmed'}
                    </Badge>
                  </div>
                </div>

                {data.period_context && (
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 text-xs space-y-1" data-testid="reconciliation-period-context">
                    <div className="flex items-center gap-1 font-medium text-slate-700"><Info className="h-3 w-3" />Audit Period / Date / Scope</div>
                    <div>Summary: {data.period_context.summary_audit_date} (period from {data.period_context.summary_previous_audit_date || '—'}, {data.period_context.summary_stock_period_days} days) · Scope {data.period_context.summary_scope}</div>
                    <div>Stocktake: {data.period_context.stocktake_period_from || '—'} → {data.period_context.stocktake_period_to || data.period_context.stocktake_session_date}</div>
                    <div className="text-slate-500 italic">{data.period_context.diagnostic}</div>
                    <div className="text-slate-400 italic">{data.period_context.note}</div>
                  </div>
                )}

                <Table data-testid="reconciliation-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Metric</TableHead>
                      <TableHead>
                        Summary <Badge variant="secondary" className="ml-1 text-[10px]">Benchmark</Badge>
                      </TableHead>
                      <TableHead>Stocktake</TableHead>
                      <TableHead>Difference</TableHead>
                      <TableHead>Diagnostic</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.comparison || []).map((row, idx) => (
                      <TableRow key={idx} data-testid={`reconciliation-row-${idx}`}>
                        <TableCell className="font-medium text-xs">{row.label}</TableCell>
                        <TableCell className="text-xs bg-slate-50/70">{fmtValue(row.summary_value, row.unit)}</TableCell>
                        <TableCell className="text-xs">{fmtValue(row.stocktake_value, row.unit)}</TableCell>
                        <TableCell className="text-xs">{row.difference != null ? fmtValue(row.difference, row.unit) : '—'}</TableCell>
                        <TableCell className="text-xs max-w-[220px]">
                          <div className="space-y-1">
                            {diagnosticBadge(row.diagnostic, row.comparable)}
                            <p className="text-[10px] text-slate-500">{row.diagnostic}</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </div>
        )}

        <div className="flex justify-end pt-1">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} data-testid="reconciliation-close-btn">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
